import type { GameState, GameEvent, GameEventType } from './types';

/**
 * Distributive Omit — applies Omit to each member of a discriminated union.
 * Standard Omit<Union, K> collapses the union to its common keys only.
 * DistributiveOmit<Union, K> preserves each member's specific fields.
 */
type DistributiveOmit<T, K extends keyof T> = T extends T ? Omit<T, K> : never;

/**
 * All valid event type discriminants for validation.
 */
const VALID_EVENT_TYPES = new Set<string>([
  'turn_start',
  'turn_end',
  'phase_change',
  'dice_roll',
  'movement_step',
  'fork_choice',
  'landing',
  'board_object_collected',
  'economy_change',
  'damage',
  'heal',
  'death',
  'respawn',
  'soul_anchor_claim',
  'soul_anchor_trigger',
  'modifier_triggered',
  'award_granted',
  'player_received_item',
  'item_cast',
  'item_hit',
  'item_missed',
  'item_blocked_stoneskin',
  'item_swap_positions',
  'forced_relocation',
  'status_effect_applied',
  'status_effect_expired',
  'stone_skin_discard_prompt',
  'npc_spawned',
  'npc_moved',
  'npc_killed_player',
  'npc_despawned',
  // Plan 4b
  'item_pull',
  'item_steal_keys',
  'item_steal_item',
  'item_dash',
  'item_dash_hit',
  'item_charge',
  'item_beam_fired',
  'item_dot_applied',
  'item_dot_tick',
  'item_dot_expired',
  'mystery_box_outcome',
  'mystery_box_teleport',
  'mystery_box_choice_required',
  'mystery_box_choice_resolved',
  'mystery_box_trapped',
  'npc_summoned',
  'soul_duel_triggered',
  'soul_duel_resolved',
]);

/**
 * Callback invoked when an event is appended to the log.
 */
export type EventLogSubscriber = (event: GameEvent) => void;

/**
 * Subscribers registry. Stored outside GameState to avoid serialization issues.
 * Keyed by a subscriber ID (auto-incrementing string) for unsubscribe support.
 */
const subscribers: Map<string, EventLogSubscriber> = new Map();

/**
 * Auto-incrementing counter for subscriber IDs.
 */
let nextSubscriberId = 0;

/**
 * Append an event to the game state's event log.
 * Automatically sets the timestamp to the current log length (monotonic counter).
 * Notifies all subscribers after appending.
 *
 * @param state - Game state whose eventLog to append to.
 * @param event - The event to append (timestamp will be set automatically).
 */
export function appendEvent(
  state: GameState,
  event: DistributiveOmit<GameEvent, 'timestamp'>,
): void {
  const timestamp = state.eventLog.length;
  const fullEvent = { ...event, timestamp } as GameEvent;
  state.eventLog.push(fullEvent);

  // Notify all subscribers
  for (const callback of subscribers.values()) {
    callback(fullEvent);
  }
}

/**
 * Query events by type.
 * Returns all events matching the given type discriminant.
 */
export function queryEventsByType<T extends GameEventType>(
  state: GameState,
  type: T,
): Extract<GameEvent, { type: T }>[] {
  return state.eventLog.filter((e) => e.type === type) as Extract<GameEvent, { type: T }>[];
}

/**
 * Query events by player ID.
 * Returns all events where playerId matches (excludes null-playerId system events).
 */
export function queryEventsByPlayer(state: GameState, playerId: string): GameEvent[] {
  return state.eventLog.filter((e) => e.playerId === playerId);
}

/**
 * Subscribe to new events. Returns a subscriber ID for unsubscribe.
 */
export function subscribe(callback: EventLogSubscriber): string {
  const id = String(nextSubscriberId++);
  subscribers.set(id, callback);
  return id;
}

/**
 * Unsubscribe by subscriber ID.
 */
export function unsubscribe(subscriberId: string): void {
  subscribers.delete(subscriberId);
}

/**
 * Clear all subscribers. Used in tests and between games.
 */
export function clearSubscribers(): void {
  subscribers.clear();
}

/**
 * Serialize the event log to a JSON-safe array.
 * Returns a copy of the eventLog array to ensure type safety.
 */
export function serializeEventLog(state: GameState): GameEvent[] {
  return [...state.eventLog];
}

/**
 * Deserialize an event log array (e.g., from a saved replay).
 * Validates each event has the required fields (type, timestamp).
 * Returns the validated GameEvent array, or throws on invalid data.
 *
 * @param data - Array of unknown objects to validate and deserialize.
 * @throws Error if any event is missing required fields or has an invalid type discriminant.
 */
export function deserializeEventLog(data: unknown[]): GameEvent[] {
  const result: GameEvent[] = [];
  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    if (typeof item !== 'object' || item === null) {
      throw new Error(`deserializeEventLog: item at index ${i} is not an object`);
    }
    const record = item as Record<string, unknown>;

    if (!('type' in record) || typeof record['type'] !== 'string') {
      throw new Error(`deserializeEventLog: item at index ${i} is missing required field 'type'`);
    }
    if (!('timestamp' in record) || typeof record['timestamp'] !== 'number') {
      throw new Error(
        `deserializeEventLog: item at index ${i} is missing required field 'timestamp'`,
      );
    }

    if (!VALID_EVENT_TYPES.has(record['type'])) {
      throw new Error(
        `deserializeEventLog: item at index ${i} has invalid type discriminant '${record['type']}'`,
      );
    }

    result.push(record as unknown as GameEvent);
  }
  return result;
}
